// app/(routes)/careers/[slug]/components/application-modal.tsx
"use client";

import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sheet, SheetContent } from "@/app/_components/ui/sheet";
import { Dialog, DialogContent } from "@/app/_components/ui/dialog";
import { Input } from "@/app/_components/ui/input";
import { Textarea } from "@/app/_components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_components/ui/select";
import { Label } from "@/app/_components/ui/label";
import {
  Check,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Upload,
  X,
  FileText,
  CheckCircle2,
} from "lucide-react";
import type { JobOpening } from "@prisma/client";
import type { FormConfig, FormField, FormStep } from "@/app/_lib/types/careers";
import { NIGERIAN_STATES } from "@/app/_lib/types/careers";
import { useIsMobile } from "@/app/_hooks/use-is-moble";
import { submitJobApplication } from "../lib/actions";
import { uploadImageToCloudinary } from "@/app/_lib/utils";
import countries from "@/app/_lib/countries.json";
import Image from "next/image";

// ── Types ─────────────────────────────────────────────────────────────────────
type FormValues = Record<string, string | File | undefined>;
type FormErrors = Record<string, string>;

// ── Validation ────────────────────────────────────────────────────────────────
function validateStep(stepFields: FormField[], values: FormValues): FormErrors {
  const errs: FormErrors = {};
  for (const field of stepFields) {
    if (!field.enabled) continue;
    const val = values[field.id];
    if (field.required) {
      const empty =
        val === undefined ||
        val === "" ||
        (typeof val === "string" && !val.trim());
      const missingFile =
        (field.type === "file" || field.builtinId === "cv") &&
        !(val instanceof File);
      if (empty || missingFile) {
        errs[field.id] = `${field.label} is required`;
        continue;
      }
    }
    if (field.builtinId === "email") {
      const s = typeof val === "string" ? val : "";
      if (s && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s))
        errs[field.id] = "Please enter a valid email address";
    }
    if (field.builtinId === "phone") {
      const s = typeof val === "string" ? val : "";
      if (s && !/^\+?[\d\s\-().]{7,20}$/.test(s))
        errs[field.id] = "Please enter a valid phone number";
    }
  }
  return errs;
}

// ── Step slide variants ───────────────────────────────────────────────────────
const stepVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 24 : -24, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -24 : 24, opacity: 0 }),
};

// ── File dropzone ─────────────────────────────────────────────────────────────
function FileDropzone({
  accept,
  value,
  error,
  onChange,
}: {
  accept?: string;
  value?: File;
  error?: string;
  onChange: (f: File | undefined) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  if (value) {
    return (
      <div className="flex items-center gap-3 bg-gray-100 border border-border  px-4 py-3">
        <FileText className="w-5 h-5 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {value.name}
          </p>
          <p className="text-xs text-muted-foreground">
            {(value.size / 1024).toFixed(0)} KB
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            onChange(undefined);
            if (ref.current) ref.current.value = "";
          }}
          className="text-muted-foreground hover:text-red-500 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <input
        ref={ref}
        type="file"
        accept={accept || ".pdf,.doc,.docx"}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onChange(f);
        }}
      />
      <div
        onClick={() => ref.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onChange(f);
        }}
        className={`flex flex-col items-center gap-2 border-2 border-dashed px-6 py-7 cursor-pointer transition-all text-center  ${
          drag
            ? "border-primary bg-primary/5"
            : error
              ? "border-red-300 bg-red-50"
              : "border-border hover:border-primary/50 bg-gray-100"
        }`}
      >
        <Upload
          className={`w-5 h-5 ${drag ? "text-primary" : "text-gray-400"}`}
        />
        <p className="text-sm text-gray-600">
          Drop file here or{" "}
          <span className="text-primary font-semibold underline">browse</span>
        </p>
        <p className="text-xs text-gray-400">PDF, DOC, DOCX (max 10 MB)</p>
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </>
  );
}

// ── Field renderer ────────────────────────────────────────────────────────────
function FieldRenderer({
  field,
  value,
  error,
  onChange,
  selectedCountry,
  onCountryChange,
}: {
  field: FormField;
  value: string | File | undefined;
  error?: string;
  onChange: (v: string | File | undefined) => void;
  selectedCountry?: string;
  onCountryChange?: (c: string) => void;
}) {
  const strVal = typeof value === "string" ? value : "";

  if (field.builtinId === "country") {
    return (
      <div className="space-y-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Select
          value={strVal}
          onValueChange={(v) => {
            onChange(v);
            onCountryChange?.(v);
          }}
        >
          <SelectTrigger
            className={` ${error ? "border-red-400" : "border-border"} bg-gray-100`}
          >
            <SelectValue placeholder="Select your country…" />
          </SelectTrigger>
          <SelectContent>
            {countries.map((c) => (
              <SelectItem key={c.name} value={c.name}>
                <div className="flex items-center gap-2">
                  <Image src={c.flag} alt={c.name} width={20} height={20} />
                  {c.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  if (field.builtinId === "state") {
    const showNigerian = selectedCountry === "Nigeria";
    if (showNigerian) {
      return (
        <div className="space-y-1.5">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </Label>
          <Select value={strVal} onValueChange={onChange}>
            <SelectTrigger
              className={` ${error ? "border-red-400" : "border-border"} bg-gray-100`}
            >
              <SelectValue placeholder="Select your state…" />
            </SelectTrigger>
            <SelectContent>
              {NIGERIAN_STATES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }
    return (
      <div className="space-y-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Input
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || "State / Province"}
          className={` ${error ? "border-red-400" : " "} bg-gray-100`}
        />
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  if (field.builtinId === "cv" || field.type === "file") {
    return (
      <div className="space-y-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <FileDropzone
          accept={field.builtinId === "cv" ? ".pdf,.doc,.docx" : undefined}
          value={value instanceof File ? value : undefined}
          error={error}
          onChange={onChange}
        />
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div className="space-y-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Select value={strVal} onValueChange={onChange}>
          <SelectTrigger
            className={` ${error ? "border-red-400" : "border-border"} bg-gray-100`}
          >
            <SelectValue
              placeholder={field.placeholder || "Select an option…"}
            />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div className="space-y-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {(field.options || []).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`px-3 py-1.5 text-sm border transition-all ${
                strVal === opt
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-foreground border-border hover:border-primary/50"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="space-y-1.5">
        <Label>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Textarea
          value={strVal}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          rows={4}
          className={` ${error ? "border-red-400" : "border-border"} bg-gray-100`}
        />
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label>
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        type={
          field.type === "number"
            ? "number"
            : field.builtinId === "email"
              ? "email"
              : field.builtinId === "phone"
                ? "tel"
                : "text"
        }
        value={strVal}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        className={` ${error ? "border-red-400" : "border-border"} bg-gray-100`}
      />
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

// ── Dot stepper ───────────────────────────────────────────────────────────────
function DotStepper({
  steps,
  currentIndex,
}: {
  steps: FormStep[];
  currentIndex: number;
}) {
  if (steps.length <= 1) return null;
  return (
    <div className="flex items-center gap-2 mb-6">
      {steps.map((step, i) => (
        <div key={step.id} className="flex items-center gap-2">
          <motion.div
            animate={{
              scale: i === currentIndex ? 1.15 : 1,
              backgroundColor:
                i < currentIndex
                  ? "hsl(92, 47%, 48%)"
                  : i === currentIndex
                    ? "hsl(92, 47%, 48%)"
                    : "transparent",
            }}
            transition={{ duration: 0.25 }}
            className={`w-2.5 h-2.5 rounded-full border-2 flex items-center justify-center ${
              i <= currentIndex ? "border-primary" : "border-border"
            }`}
          >
            {i < currentIndex && (
              <Check className="w-1.5 h-1.5 text-white stroke-[3]" />
            )}
          </motion.div>
          {i < steps.length - 1 && (
            <div
              className={`h-px w-8 transition-colors duration-300 ${
                i < currentIndex ? "bg-primary" : "bg-border"
              }`}
            />
          )}
        </div>
      ))}
      <span className="ml-1 text-xs text-muted-foreground">
        {steps[currentIndex]?.title}
      </span>
    </div>
  );
}

// ── Inner form content ────────────────────────────────────────────────────────
function ApplicationFormContent({
  job,
  formConfig,
  onSuccess,
}: {
  job: JobOpening;
  formConfig: FormConfig;
  onSuccess: () => void;
}) {
  const sortedSteps = [...formConfig.steps].sort((a, b) => a.order - b.order);
  const [stepIndex, setStepIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [values, setValues] = useState<FormValues>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [selectedCountry, setSelectedCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const currentStep = sortedSteps[stepIndex];
  const currentFields = formConfig.fields
    .filter((f) => f.enabled && f.stepId === currentStep?.id)
    .sort((a, b) => a.order - b.order);

  const isLastStep = stepIndex === sortedSteps.length - 1;

  const goNext = () => {
    const errs = validateStep(currentFields, values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setDir(1);
    setStepIndex((i) => i + 1);
  };

  const goBack = () => {
    setErrors({});
    setDir(-1);
    setStepIndex((i) => i - 1);
  };

  const handleSubmit = async () => {
    const errs = validateStep(currentFields, values);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const fd = new FormData();
      fd.append("jobSlug", job.slug);

      for (const field of formConfig.fields.filter((f) => f.enabled)) {
        const val = values[field.id];

        if (val instanceof File) {
          const isImage = val.type.startsWith("image/");
          if (isImage) {
            const publicId = await uploadImageToCloudinary(val);
            fd.append(field.id, publicId);
          } else {
            fd.append(field.id, val);
          }
        } else if (val) {
          fd.append(field.id, val as string);
        }
      }

      const result = await submitJobApplication(fd);
      if (result.success) {
        onSuccess();
      } else {
        setSubmitError(
          result.error || "Something went wrong. Please try again.",
        );
      }
    } catch (error) {
      console.error("Form submission catch error:", error);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col flex-1">
      <DotStepper steps={sortedSteps} currentIndex={stepIndex} />

      <div className="relative flex-1 overflow-hidden min-h-[280px]">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={currentStep?.id}
            custom={dir}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="space-y-4"
          >
            {currentFields.map((field) => (
              <FieldRenderer
                key={field.id}
                field={field}
                value={values[field.id]}
                error={errors[field.id]}
                onChange={(v) => {
                  setValues((prev) => ({ ...prev, [field.id]: v }));
                  if (errors[field.id])
                    setErrors((prev) => {
                      const n = { ...prev };
                      delete n[field.id];
                      return n;
                    });
                }}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      {submitError && (
        <p className="text-xs text-red-500 mt-3">{submitError}</p>
      )}

      {/* Navigation */}
      <div className="flex gap-3 mt-6 pt-4 border-t border-border">
        {stepIndex > 0 && (
          <button
            type="button"
            onClick={goBack}
            className="flex items-center gap-1.5 px-4 h-12 border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        )}
        <button
          type="button"
          onClick={isLastStep ? handleSubmit : goNext}
          disabled={submitting}
          className="flex-1 h-12 bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-60 active:scale-[0.98]"
        >
          {submitting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : isLastStep ? (
            <>
              Submit Application <ChevronRight className="w-4 h-4" />
            </>
          ) : (
            <>
              Continue <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ── Success state ─────────────────────────────────────────────────────────────
function SuccessState({ jobTitle }: { jobTitle: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="flex flex-col items-center gap-5 py-6 text-center"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{
          delay: 0.15,
          type: "spring",
          stiffness: 240,
          damping: 20,
        }}
        className="w-16 h-16 bg-primary flex items-center justify-center shadow-lg shadow-primary/20"
      >
        <Check className="w-8 h-8 text-white stroke-[2.5]" />
      </motion.div>
      <div>
        <h3 className="text-xl font-bold">Application Sent!</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-xs mx-auto leading-relaxed">
          Thanks for applying for{" "}
          <span className="font-semibold text-foreground">{jobTitle}</span>.
          We&apos;ll review it and get back to you within 5–7 business days.
        </p>
      </div>
    </motion.div>
  );
}

// ── Modal shell ───────────────────────────────────────────────────────────────
export default function ApplicationModal({
  open,
  onClose,
  job,
  formConfig,
}: {
  open: boolean;
  onClose: () => void;
  job: JobOpening;
  formConfig: FormConfig;
}) {
  const [success, setSuccess] = useState(false);
  const isMobile = useIsMobile(768);

  const handleClose = () => {
    onClose();
    setTimeout(() => setSuccess(false), 350);
  };

  const innerContent = (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">
          {job.title}
        </p>
        <h2 className="text-2xl font-bold tracking-tight">
          {success ? "All done!" : "Apply for this Role"}
        </h2>
      </div>

      {success ? (
        <SuccessState jobTitle={job.title} />
      ) : (
        <ApplicationFormContent
          job={job}
          formConfig={formConfig}
          onSuccess={() => setSuccess(true)}
        />
      )}
    </div>
  );

  // ── Mobile: bottom sheet ──────────────────────────────────────────────────
  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={(v) => !v && handleClose()}>
        <SheetContent
          side="bottom"
          className="p-0 !max-h-[92dvh] border-none outline-none"
          style={{
            maxHeight: "92dvh",
            background: "hsl(var(--background))",
          }}
          hideCloseButton={true}
        >
          <div className="flex flex-col h-full">
            {/* Drag handle */}
            <motion.div
              className="flex-shrink-0 flex items-center justify-center pt-3 pb-2 cursor-grab active:cursor-grabbing touch-none"
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.4 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 60 || info.velocity.y > 400) handleClose();
              }}
            >
              <div className="w-10 h-1 rounded-full bg-border" />
            </motion.div>

            <div className=" overflow-y-auto max-h-[92dvh] px-5 pb-14 pt-5">
              {innerContent}
            </div>
            <div style={{ height: "env(safe-area-inset-bottom, 0px)" }} />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // ── Desktop: centred dialog ───────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-[600px] p-0 overflow-hidden border-none rounded-none sm:rounded-none bg-background shadow-2xl">
        <motion.div
          initial={{ opacity: 0, scale: 0.97, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.97, y: 8 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          className="p-8"
        >
          {innerContent}
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
